import type {
  StaffCatalogItemPage,
  StaffItineraryDetail,
  StaffItineraryRow,
  StaffRequestStateValue,
} from "@lafayette/schemas";
import { useQueries, useQuery } from "@tanstack/react-query";

import { useItineraryRuntime } from "./runtime";

// The reads of the itinerary pages. A staff answer holds customer plans, so nothing stays in the cache after the page lets go
// (`gcTime: 0`) and nothing is written to browser storage. A failed read is not retried by the library: the page shows the error
// and offers a retry. A linked request's state is read from the request read path (live), never copied from the plan.

export const itineraryKeys = {
  all: ["staff-itinerary"] as const,
  revisions: (tripId: string) => ["staff-itinerary", "revisions", tripId] as const,
  detail: (itineraryId: string) => ["staff-itinerary", "detail", itineraryId] as const,
  request: (requestId: string) => ["staff-itinerary", "request", requestId] as const,
  catalogSearch: (q: string) => ["staff-itinerary", "catalog-search", q] as const,
};

const READ_OPTIONS = { gcTime: 0, retry: false, refetchOnWindowFocus: false } as const;

/** MD-I7. Every itinerary of a trip, oldest first. */
export function useTripRevisions(tripId: string, enabled: boolean) {
  const { api } = useItineraryRuntime();
  return useQuery<readonly StaffItineraryRow[]>({
    queryKey: itineraryKeys.revisions(tripId),
    queryFn: ({ signal }) => api.listRevisions(tripId, { signal }),
    enabled,
    ...READ_OPTIONS,
  });
}

/** MD-I8. One itinerary with its issues, its newest report, its revisions and the change audit. */
export function useItineraryDetail(itineraryId: string | null, enabled: boolean) {
  const { api } = useItineraryRuntime();
  return useQuery<StaffItineraryDetail>({
    queryKey: itineraryKeys.detail(itineraryId ?? "none"),
    queryFn: ({ signal }) => api.getDetail(itineraryId as string, { signal }),
    enabled: enabled && itineraryId !== null,
    ...READ_OPTIONS,
  });
}

// The catalog state of an item (published, no longer published, missing) comes with the plan (MD-I8 `view.catalogState`): one read for the
// whole plan, no read per item.

/** The published catalog items that match some words (the add-item dialog). */
export function useCatalogSearch(words: string) {
  const { catalog } = useItineraryRuntime();
  const q = words.trim();
  return useQuery<StaffCatalogItemPage>({
    queryKey: itineraryKeys.catalogSearch(q),
    queryFn: ({ signal }) => catalog.listItems({ q, status: ["PUBLISHED"], limit: 10 }, { signal }),
    enabled: q.length >= 2,
    ...READ_OPTIONS,
  });
}

// --- live request state -------------------------------------------------------------------------------------------------------

export type RequestLive =
  | Readonly<{ status: "OK"; state: StaffRequestStateValue | string }>
  | Readonly<{ status: "LOADING" }>
  /** The request could not be read (no access, deleted, or a network failure): the page says so and shows no state. */
  | Readonly<{ status: "UNKNOWN" }>;

/** The state of each linked request, read from the request read path on mount, on focus, on a timer and after every command. */
export function useRequestStates(requestIds: readonly string[]): ReadonlyMap<string, RequestLive> {
  const { requests, requestRefreshMs } = useItineraryRuntime();
  const results = useQueries({
    queries: requestIds.map((requestId) => ({
      queryKey: itineraryKeys.request(requestId),
      queryFn: async ({ signal }: { signal: AbortSignal }) => {
        const detail = await requests.get(requestId, { signal });
        const state = (detail.request as Readonly<Record<string, unknown>>).state;
        return typeof state === "string" ? state : null;
      },
      gcTime: 0,
      retry: false,
      refetchOnWindowFocus: true,
      refetchInterval: requestRefreshMs > 0 ? requestRefreshMs : (false as const),
    })),
  });
  const out = new Map<string, RequestLive>();
  requestIds.forEach((requestId, index) => {
    const result = results[index];
    out.set(
      requestId,
      result === undefined || result.isPending
        ? { status: "LOADING" }
        : result.isError || result.data === null || result.data === undefined
          ? { status: "UNKNOWN" }
          : { status: "OK", state: result.data },
    );
  });
  return out;
}
